import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { governanceOverlaysService } from '../services/governanceOverlaysService';
import { GovernanceOverlay } from '../types/governanceOverlays';
import { Users, Clock, AlertCircle, CheckCircle, Star, FileText, Settings, Shield } from 'lucide-react';
import DataCard from '../components/common/DataCard';

const GovernanceOverlays: React.FC = () => {
  const { accessToken, setUserAndToken } = useUser();
  const [overlays, setOverlays] = useState<GovernanceOverlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOverlays = async () => {
      try {
        setLoading(true);
        const data = await governanceOverlaysService.getGovernanceOverlays(accessToken, setUserAndToken);
        // Sort by 'order_to_evaluate' entry directly from descriptions
        const sortedData = data.sort((a, b) => {
          const aOrder = Number(
            a.descriptions.find(d => d.is_active && d.key === 'order_to_evaluate')?.value ?? 0
          );
          const bOrder = Number(
            b.descriptions.find(d => d.is_active && d.key === 'order_to_evaluate')?.value ?? 0
          );
          return (Number.isNaN(aOrder) ? 0 : aOrder) - (Number.isNaN(bOrder) ? 0 : bOrder);
        });
        setOverlays(sortedData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch governance overlays');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchOverlays();
    }
  }, [accessToken, setUserAndToken]);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading governance overlays...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Overlays</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-slate-900 mb-1 px-2">Manage governance overlay rules and protocols</h2>
        </div>
        <Link
          to="/governance-overlays/create"
          className="bg-[#4a67b7] hover:bg-primary/90 text-white px-6 py-2 rounded-lg flex items-center shadow transition"
        >
          + Add Overlay
        </Link>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg text-slate-900">All Governance Overlays</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-500">Total Overlays:</span>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {overlays.length}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {overlays.map((overlay) => (
              <DataCard
                key={overlay.id}
                data={overlay}
                dataType="governanceOverlay"
                showLink={true}
                linkPath={`/governance-overlays/${overlay.id}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {overlays.length === 0 && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Overlays Found</h3>
            <p className="text-slate-600">No governance overlays are currently configured.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GovernanceOverlays;
